// Service for handling email operations with Gmail API
import { collection, addDoc, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Firebase db is imported from '../firebase'
const EMAILS_COLLECTION = 'emails';
const PROCESSED_LABEL = 'HOMI_PROCESSED';

// Fetch emails from Gmail API with token validation
export const fetchEmails = async (accessToken, maxResults = 100, includeProcessed = false) => {
  try {
    // Validate the token
    if (!accessToken) {
      throw new Error('No access token provided. Please sign in again.');
    }
    
    // Check if token is valid
    const tokenIsValid = await validateAccessToken(accessToken);
    if (!tokenIsValid) {
      throw new Error('TOKEN_EXPIRED');
    }
    
    console.log(`Fetching emails (max: ${maxResults}, includeProcessed: ${includeProcessed})`);
    
    // Build the API URL for listing messages
    const apiUrl = new URL('https://www.googleapis.com/gmail/v1/users/me/messages');
    apiUrl.searchParams.append('maxResults', maxResults.toString());
    
    // Build query to include emails from specific domains or containing their addresses
    // This will catch both direct emails and forwarded emails that contain these addresses
    let queryParts = [
      '(from:notifications.seesaw.me OR from:arborschool.ae OR from:thearborschool.ae OR "notifications.seesaw.me" OR "arborschool.ae" OR "thearborschool.ae")'
    ];
    
    // Add date filter to only include emails from the last 45 days
    const today = new Date();
    const fortyFiveDaysAgo = new Date(today.getTime() - (45 * 24 * 60 * 60 * 1000));
    const dateFilter = `${fortyFiveDaysAgo.getFullYear()}/${fortyFiveDaysAgo.getMonth() + 1}/${fortyFiveDaysAgo.getDate()}`;
    queryParts.push(`after:${dateFilter}`);
    
    // If we don't want to include processed emails, add a query parameter
    // to exclude emails with our processed label
    if (!includeProcessed) {
      queryParts.push(`-label:${PROCESSED_LABEL}`);
    }
    
    const query = queryParts.join(' ');
    apiUrl.searchParams.append('q', query);
    
    // Make the API request to list messages
    const response = await fetch(apiUrl.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('TOKEN_EXPIRED');
      }
      
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        throw new Error(`Gmail API error: ${response.statusText}`);
      }
      
      throw new Error(`Gmail API error: ${errorData.error?.message || response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.messages || data.messages.length === 0) {
      console.log('No emails found');
      return [];
    }
    
    console.log(`Found ${data.messages.length} email(s)`);
    
    // Get detailed information for each message
    const emailDetails = await Promise.all(
      data.messages.map(message => getEmailDetails(accessToken, message.id))
    );
    
    // Filter out any failed fetches
    const validEmails = emailDetails.filter(email => email !== null);
    
    return validEmails;
  } catch (error) {
    console.error('Error fetching emails:', error);
    throw error;
  }
};

// Get detailed information for a specific email
const getEmailDetails = async (accessToken, messageId) => {
  try {
    const apiUrl = `https://www.googleapis.com/gmail/v1/users/me/messages/${messageId}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      console.error(`Failed to fetch details for email ${messageId}: ${response.statusText}`);
      return null;
    }
    
    const data = await response.json();
    
    // Parse the email data into a more usable format
    return parseEmailData(data);
  } catch (error) {
    console.error(`Error fetching details for email ${messageId}:`, error);
    return null;
  }
};

// Parse Gmail API response into a more usable format
const parseEmailData = (emailData) => {
  try {
    const headers = emailData.payload.headers.reduce((acc, header) => {
      acc[header.name.toLowerCase()] = header.value;
      return acc;
    }, {});
    
    // Extract the email body
    let body = '';
    let hasAttachments = false;
    
    // Parse parts to find body content and check for attachments
    if (emailData.payload.parts) {
      // Multi-part email (common for HTML emails)
      const textPart = emailData.payload.parts.find(part => 
        part.mimeType === 'text/plain' || part.mimeType === 'text/html'
      );
      
      if (textPart && textPart.body.data) {
        body = decodeBase64(textPart.body.data);
      }
      
      // Check for attachments
      hasAttachments = emailData.payload.parts.some(part => 
        part.filename && part.filename.length > 0 && part.body && part.body.attachmentId
      );
    } else if (emailData.payload.body.data) {
      // Simple email
      body = decodeBase64(emailData.payload.body.data);
    }
    
    // Check if this email has already been processed
    const labels = emailData.labelIds || [];
    const isProcessed = labels.includes(PROCESSED_LABEL);
    
    return {
      id: emailData.id,
      threadId: emailData.threadId,
      subject: headers.subject || '(No Subject)',
      from: headers.from || '',
      to: headers.to || '',
      date: headers.date ? new Date(headers.date) : new Date(),
      body: body,
      snippet: emailData.snippet || '',
      isProcessed: isProcessed,
      labels: labels,
      // Add a cleaned up version of the from field (just the name or email)
      fromName: extractNameFromEmailField(headers.from || ''),
      // Attachment information
      hasAttachments: hasAttachments,
      // Keep track of raw email data in case we need it
      raw: emailData
    };
  } catch (error) {
    console.error('Error parsing email data:', error);
    return null;
  }
};

// Helper function to decode base64 URL encoded strings
const decodeBase64 = (encodedString) => {
  try {
    // Replace URL-safe characters
    const sanitized = encodedString.replace(/-/g, '+').replace(/_/g, '/');
    return atob(sanitized);
  } catch (error) {
    console.error('Error decoding base64 email content:', error);
    return '';
  }
};

// Extract a readable name from email header fields like "John Doe <john@example.com>"
const extractNameFromEmailField = (emailField) => {
  if (!emailField) return '';
  
  // Try to match a pattern like "Name <email>"
  const match = emailField.match(/^([^<]+)<([^>]+)>/);
  
  if (match && match[1]) {
    // Return the name part, trimmed
    return match[1].trim();
  }
  
  // If no match, just return the whole field
  return emailField;
};


// Function to validate if a token is still accepted by Google
async function validateAccessToken(token) {
  try {
    // First check if token has expired based on stored expiry time
    const storedExpiry = localStorage.getItem('emailTokenExpiry');
    const now = new Date().getTime();
    
    if (storedExpiry) {
      const expiry = parseInt(storedExpiry);
      
      // If token has expired based on our stored time, return false immediately
      if (now >= expiry) {
        console.log(`❌ [Email/Gmail] Token has expired:`);
        console.log(`  - Current time: ${new Date(now).toLocaleTimeString()}`);
        console.log(`  - Expiry time: ${new Date(expiry).toLocaleTimeString()}`);
        return false;
      }
      
      // If we're within 5 minutes of expiry, also return false to trigger refresh
      const fiveMinutesBeforeExpiry = expiry - (5 * 60 * 1000);
      if (now >= fiveMinutesBeforeExpiry) {
        const minutesLeft = (expiry - now) / 1000 / 60;
        console.log(`⚠️ [Email/Gmail] Token expiring soon:`);
        console.log(`  - Current time: ${new Date(now).toLocaleTimeString()}`);
        console.log(`  - Expires in: ${minutesLeft.toFixed(1)} minutes`);
        console.log(`  - Triggering early refresh`);
        return false;
      }
    }
    
    // Check if we've validated this token recently (in the last 15 minutes)
    const lastValidated = localStorage.getItem('emailTokenLastValidated');
    
    if (lastValidated) {
      const lastValidatedTime = parseInt(lastValidated);
      const fifteenMinutesAgo = now - (15 * 60 * 1000);
      
      // If we validated less than 15 minutes ago, assume it's still valid
      if (lastValidatedTime > fifteenMinutesAgo) {
        console.log('Using cached email token validation from the last 15 minutes');
        return true;
      }
    }
    
    // Use the tokeninfo endpoint to check if token is still valid
    console.log('Validating email token with Google API...');
    const response = await fetch(`https://www.googleapis.com/oauth2/v3/tokeninfo?access_token=${token}`);
    
    // If response is OK, token is valid - cache the result
    if (response.ok) {
      localStorage.setItem('emailTokenLastValidated', now.toString());
    }
    
    return response.ok;
  } catch (error) {
    console.error('Error validating email token:', error);
    return false;
  }
}

// Mark an email as processed both in Gmail (with a label) and in our database
export const markEmailAsProcessed = async (accessToken, emailId) => {
  try {
    if (!accessToken) {
      throw new Error('No Gmail access token provided. Please sign in to Gmail first.');
    }
    
    if (!emailId) {
      throw new Error('No email ID provided for processing.');
    }
    
    // First, ensure our custom label exists in Gmail
    let labelId;
    let skipLabelProcessing = false;
    
    try {
      labelId = await ensureProcessedLabelExists(accessToken);
    } catch (labelError) {
      // Log the specific error for debugging
      console.warn('Label operation failed:', labelError.message);
      
      // For rate limit errors, still fail fast to inform user
      if (labelError.message.includes('rate limit')) {
        throw new Error(`Gmail rate limit exceeded while managing labels. ${labelError.message}`);
      } 
      // For permission and auth errors, also fail fast as these need user action
      else if (labelError.message.includes('permissions')) {
        throw new Error(`Gmail permission error. ${labelError.message}`);
      } else if (labelError.message.includes('expired')) {
        throw new Error(`Gmail access expired. ${labelError.message}`);
      } 
      // For other errors (network issues, temporary Gmail problems), use fallback
      else {
        console.warn('Label operations unavailable, proceeding without Gmail labeling. Email will still be processed and stored.');
        skipLabelProcessing = true;
      }
    }
    
    if (!labelId && !skipLabelProcessing) {
      console.warn('Unable to create or find the processed label in Gmail. Proceeding without Gmail labeling.');
      skipLabelProcessing = true;
    }
    
    // Apply the label to the email (only if label operations are available)
    if (!skipLabelProcessing && labelId) {
      try {
        const apiUrl = `https://www.googleapis.com/gmail/v1/users/me/messages/${emailId}/modify`;
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            addLabelIds: [labelId]
          })
        });
        
        if (!response.ok) {
          let errorMessage = 'Failed to mark email as processed';
          let errorDetails = null;
          
          try {
            errorDetails = await response.json();
            errorMessage = errorDetails.error?.message || errorMessage;
          } catch (parseError) {
            errorMessage = `${errorMessage} (Status: ${response.status})`;
          }
          
          // Handle specific error types for email modification
          if (response.status === 429 || (errorDetails && errorDetails.error?.code === 429)) {
            throw new Error('Gmail API rate limit exceeded while marking email as processed. Please wait a few minutes before processing more emails.');
          } else if (response.status === 403) {
            throw new Error('Insufficient permissions to modify Gmail messages. Please re-authorize the application.');
          } else if (response.status === 401) {
            throw new Error('Gmail access token expired. Please sign in again.');
          } else if (response.status === 404) {
            throw new Error(`Email with ID ${emailId} not found in Gmail. It may have been deleted or moved.`);
          }
          
          throw new Error(`Failed to mark email as processed: ${errorMessage}`);
        }
        
        console.log(`Email ${emailId} marked as processed in Gmail`);
      } catch (labelingError) {
        // If labeling fails but it's not a critical error, log warning and continue
        if (labelingError.message.includes('rate limit') || 
            labelingError.message.includes('permissions') || 
            labelingError.message.includes('expired')) {
          // Re-throw critical errors that require user action
          throw labelingError;
        } else {
          // Log non-critical labeling errors but don't fail the entire operation
          console.warn(`Warning: Could not label email ${emailId} in Gmail:`, labelingError.message);
          console.warn('Email processing will continue without Gmail labeling.');
        }
      }
    } else {
      console.log(`Email ${emailId} processed successfully (Gmail labeling skipped)`);
    }
    
    return true;
  } catch (error) {
    console.error('Error marking email as processed:', error);
    
    // Re-throw with preserved context
    throw error;
  }
};

// Ensure our custom "Processed" label exists in Gmail
const ensureProcessedLabelExists = async (accessToken, retryCount = 0, maxRetries = 3) => {
  try {
    // First, check if the label already exists
    const labelsUrl = 'https://www.googleapis.com/gmail/v1/users/me/labels';
    
    const response = await fetch(labelsUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      // Parse error response for specific error handling
      let errorMessage = 'Failed to fetch labels';
      let errorDetails = null;
      
      try {
        errorDetails = await response.json();
        errorMessage = errorDetails.error?.message || errorMessage;
      } catch (parseError) {
        errorMessage = `${errorMessage} (Status: ${response.status})`;
      }
      
      // Handle specific error types
      if (response.status === 429 || (errorDetails && errorDetails.error?.code === 429)) {
        console.warn(`Gmail API rate limit exceeded when fetching labels (attempt ${retryCount + 1}/${maxRetries + 1})`);
        
        if (retryCount < maxRetries) {
          // Exponential backoff: wait 2^retryCount seconds
          const waitTime = Math.pow(2, retryCount) * 1000;
          console.log(`Retrying in ${waitTime}ms...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return ensureProcessedLabelExists(accessToken, retryCount + 1, maxRetries);
        } else {
          throw new Error('Gmail API rate limit exceeded. Please wait a few minutes before trying again.');
        }
      } else if (response.status === 403) {
        throw new Error('Insufficient permissions to access Gmail labels. Please re-authorize the application.');
      } else if (response.status === 401) {
        throw new Error('Gmail access token expired. Please sign in again.');
      }
      
      throw new Error(errorMessage);
    }
    
    const labelsData = await response.json();
    
    // Look for our processed label
    const existingLabel = labelsData.labels.find(label => 
      label.name === PROCESSED_LABEL
    );
    
    if (existingLabel) {
      console.log('Found existing processed label:', existingLabel.id);
      return existingLabel.id;
    }
    
    // If label doesn't exist, create it
    console.log('Creating new processed label...');
    
    const createResponse = await fetch(labelsUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: PROCESSED_LABEL,
        labelListVisibility: 'labelShow',
        messageListVisibility: 'show'
      })
    });
    
    if (!createResponse.ok) {
      // Parse error response for specific error handling
      let errorMessage = 'Failed to create processed label';
      let errorDetails = null;
      
      try {
        errorDetails = await createResponse.json();
        errorMessage = errorDetails.error?.message || errorMessage;
      } catch (parseError) {
        errorMessage = `${errorMessage} (Status: ${createResponse.status})`;
      }
      
      // Handle specific error types for label creation
      if (createResponse.status === 429 || (errorDetails && errorDetails.error?.code === 429)) {
        console.warn(`Gmail API rate limit exceeded when creating label (attempt ${retryCount + 1}/${maxRetries + 1})`);
        
        if (retryCount < maxRetries) {
          // Exponential backoff: wait 2^retryCount seconds
          const waitTime = Math.pow(2, retryCount) * 1000;
          console.log(`Retrying in ${waitTime}ms...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return ensureProcessedLabelExists(accessToken, retryCount + 1, maxRetries);
        } else {
          throw new Error('Gmail API rate limit exceeded while creating label. Please wait a few minutes before trying again.');
        }
      } else if (createResponse.status === 403) {
        throw new Error('Insufficient permissions to create Gmail labels. Please re-authorize the application.');
      } else if (createResponse.status === 401) {
        throw new Error('Gmail access token expired. Please sign in again.');
      }
      
      throw new Error(errorMessage);
    }
    
    const newLabel = await createResponse.json();
    console.log('Created new processed label:', newLabel.id);
    
    return newLabel.id;
  } catch (error) {
    console.error('Error ensuring processed label exists:', error);
    
    // Re-throw the error with context if it's not already a descriptive error
    if (error.message.includes('rate limit') || 
        error.message.includes('permissions') || 
        error.message.includes('expired')) {
      throw error;
    } else {
      throw new Error(`Failed to manage Gmail labels: ${error.message}`);
    }
  }
};

// Store email in Firebase
export const storeEmailInFirebase = async (email, extractedEvents = [], userId = null) => {
  try {
    // Get the current user ID if not provided
    if (!userId) {
      // Try to get it from window global variable that might be set in the app
      userId = window.currentUserId || null;
    }
    
    // Store email directly without duplicate checking
    // Duplicate prevention is handled by Gmail label system
    const emailsRef = collection(db, EMAILS_COLLECTION);
    
    // Store new email in Firebase
    const timestamp = new Date();
    
    const docRef = await addDoc(emailsRef, {
      id: email.id,
      threadId: email.threadId,
      subject: email.subject,
      from: email.from,
      fromName: email.fromName,
      date: email.date,
      snippet: email.snippet,
      body: email.body,
      extractedEvents: extractedEvents || [],
      processed: true,
      createdAt: timestamp,
      lastProcessed: timestamp,
      userId: userId // Store the user ID with the email
    });
    
    console.log(`Email stored in Firebase with Firestore ID: ${docRef.id}, Gmail ID: ${email.id}`);
    return docRef.id;
  } catch (error) {
    console.error('Error storing email in Firebase:', error);
    throw error;
  }
};

// Extract potential date information from email body
export const extractDateFromEmail = async (emailBody, emailSubject, API_KEY, emailFrom = '') => {
  try {
    // Use the emailDateExtractor service
    const { extractDatesFromEmail } = await import('./emailDateExtractor');
    
    console.log('Extracting dates from email content...');
    console.log('Email from:', emailFrom);
    
    // Call the specialized extractor with the API key and email sender info
    const extractedEvents = await extractDatesFromEmail(emailBody, emailSubject, API_KEY, emailFrom);
    
    return extractedEvents;
  } catch (error) {
    console.error('Error extracting dates from email:', error);
    return [];
  }
};

// Get email body by email ID from Firebase
export const getEmailById = async (emailId, userId) => {
  try {
    const emailsRef = collection(db, EMAILS_COLLECTION);
    const q = query(
      emailsRef, 
      where("id", "==", emailId),
      where("userId", "==", userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const emailData = querySnapshot.docs[0].data();
      return {
        id: querySnapshot.docs[0].id,
        ...emailData
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching email by ID:', error);
    return null;
  }
};

// Extract events from email using the main app's date extraction functionality
export const extractEventsFromEmail = async (email) => {
  try {
    // Get the email content (combine subject and body for more context)
    const emailContent = `Subject: ${email.subject}\n\n${email.body}`;
    
    // Import the main App's date extraction method by making a call to OpenAI
    // Reusing the same prompt and processing logic from the App component
    const API_KEY = process.env.REACT_APP_OPENAI_API_KEY || window.OPENAI_API_KEY;
    
    if (!API_KEY) {
      throw new Error('OpenAI API key not found');
    }
    
    // Get current date info for context (mirroring the logic in App.js)
    const now = new Date();
    const dateInfo = {
      day: now.getDate(),
      month: now.getMonth() + 1,
      year: now.getFullYear(),
      dayOfWeek: now.getDay(),
      dayNames: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      monthNames: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
    };
    
    // Make the API call with the same prompt from App.js extractDates function
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-4o",
        messages: [
          {
            role: "system",
            content: `
            Extract all dates, times, events, locations, participants, and categories from the provided text.

            Today is ${dateInfo.monthNames[dateInfo.month-1]} ${dateInfo.day}, ${dateInfo.year} (${dateInfo.dayNames[dateInfo.dayOfWeek]}).

            For dates:
            - Handle relative terms like "today", "tomorrow", "next Monday", etc.
            - When "next week" is mentioned without a specific day:
              * Interpret it as referring to next Monday by default
              * Example: "The meeting is next week" should be interpreted as next Monday
              * If any day is specified with "next week" (e.g., "next week on Wednesday"), use that specific day instead
            - If a year is not mentioned, assume ${dateInfo.year}
            - If a month is not mentioned, assume the current month (${dateInfo.month})
            - If only a day of week is mentioned (e.g., "on Tuesday"), determine the next occurrence of that day
            - If a date appears to be in the past for this year, consider if it might refer to next year

            For times:
            - Extract time information when available (e.g., "3:00 PM", "15:30", "morning", "noon", "afternoon")
            - Handle time ranges (e.g., "from 9AM to 5PM")

            For locations:
            - Extract specific location information when mentioned (e.g., addresses, venue names, online/virtual)
            - If no location is explicitly mentioned, set location to null

            For context:
            - Create a short description that captures the context or purpose of the event based on surrounding text
            - Limit descriptions to 1-2 sentences to keep them concise
            - Include any requirements or details if mentioned
            - Do NOT include location, participant names, or category information in the description, as these will be extracted separately

            For participants:
            - Extract names of people the event is for (e.g., "John's birthday", "meeting with Sarah", "appointment for the kids")
            - If no specific person is mentioned, set the participant to null

            For event categories:
            - Identify the type of event based on contextual clues
            - Categorize as one of: "work", "school", "sport", or "life"
            - "work": work-related meetings, deadlines, professional events
            - "school": classes, academic deadlines, school-related activities
            - "sport": athletic activities, games, matches, workouts
            - "life": social activities like dinners, parties, friend meetings, personal appointments
            - Every event MUST be classified as one of these categories
            - If unable to determine a clear category, set to "life"

            Return a JSON object with a property called 'events' containing an array where each item has:
            - day (numeric)
            - month (numeric)
            - year (numeric)
            - hasTime (boolean) - whether a specific time was mentioned
            - hour (numeric, 0-23) - if a time was mentioned
            - minute (numeric, 0-59) - if a time was mentioned
            - isAllDay (boolean) - true if no specific time was mentioned
            - timeText (string) - the original time text, or general time of day mentioned
            - eventName (string)
            - description (string) - a concise description derived from the context
            - location (string or null) - specific location information if mentioned, or null if no location was specified
            - participant (string or null) - name of who the event is for/with, or null if not specified
            - category (string) - one of: "work", "school", "sport", "life", "other"
            - originalDateText (string) - the original date text as it appeared in the input
            `
          },
          {
            role: "user",
            content: emailContent
          }
        ],
        response_format: { type: "json_object" }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to extract events from email');
    }

    const data = await response.json();
    
    // Parse the response and extract events (similar to App.js)
    const content = JSON.parse(data.choices[0].message.content);
    
    if (Array.isArray(content.events) && content.events.length > 0) {
      // Prepare events with timestamp and email source information
      const newEvents = content.events.map(event => ({
        ...event,
        category: event.category || 'life', // Ensure every event has a category
        createdAt: new Date().toISOString(), // Add timestamp for sorting
        source: 'email',
        emailId: email.id,
        emailSubject: email.subject
      }));
      
      // Get the current user ID if available
      const userId = window.currentUserId || null;
      
      // Store the extracted events in the email document for future reference
      await storeEmailInFirebase(email, newEvents, userId);
      
      // Mark the email as processed in Gmail
      if (email.id && window.EMAIL_ACCESS_TOKEN) {
        try {
          await markEmailAsProcessed(window.EMAIL_ACCESS_TOKEN, email.id);
        } catch (error) {
          console.warn('Could not mark email as processed:', error);
          // Continue anyway as this is not critical
        }
      }
      
      return newEvents;
    }
    
    return [];
  } catch (error) {
    console.error('Error extracting events from email:', error);
    throw error;
  }
};